/**
 * Auth store - JWT held in memory only (never persisted to localStorage).
 * Closing the tab clears the session, requiring re-authentication.
 *
 * Registers a refresh handler with the API client so that 401 responses
 * trigger an automatic token refresh attempt before failing.
 */

import { create } from 'zustand';
import { setRefreshHandler, getApiBase } from '../api/client';

interface AuthState {
  /** JWT token - null when not authenticated */
  token: string | null;
  /** Refresh token for silent renewal */
  refreshToken: string | null;
  /** Whether a login request is in progress */
  isLoading: boolean;
  /** Error message from last login attempt */
  error: string | null;
  /** Whether the refresh handler has been registered */
  refreshRegistered: boolean;

  login: (passphrase: string, apiBase: string) => Promise<boolean>;
  logout: () => void;
  setTokens: (token: string, refreshToken: string) => void;
  clearError: () => void;
  tryRefresh: (apiBase?: string) => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set, get) => {
  // Register the refresh handler once. The handler reads tokens from the store
  // and attempts a silent refresh, returning the new token or null.
  const ensureRefreshHandler = () => {
    if (get().refreshRegistered) return;
    setRefreshHandler(async () => {
      const success = await get().tryRefresh();
      return success ? get().token : null;
    });
    set({ refreshRegistered: true });
  };

  return {
    token: null,
    refreshToken: null,
    isLoading: false,
    error: null,
    refreshRegistered: false,

    login: async (passphrase: string, apiBase: string) => {
      ensureRefreshHandler();
      set({ isLoading: true, error: null });
      try {
        const res = await fetch(`${apiBase}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ passphrase }),
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          set({ isLoading: false, error: body.error || 'Invalid passphrase' });
          return false;
        }

        const { token, refreshToken } = (await res.json()) as {
          token: string;
          refreshToken: string;
        };
        set({ token, refreshToken, isLoading: false, error: null });
        return true;
      } catch {
        set({ isLoading: false, error: 'Network error. Please try again.' });
        return false;
      }
    },

    logout: () => {
      set({ token: null, refreshToken: null, error: null });
    },

    setTokens: (token: string, refreshToken: string) => {
      set({ token, refreshToken });
    },

    clearError: () => set({ error: null }),

    tryRefresh: async (apiBase?: string) => {
      const { refreshToken } = get();
      if (!refreshToken) return false;

      const base = apiBase || getApiBase();
      try {
        const res = await fetch(`${base}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (!res.ok) {
          set({ token: null, refreshToken: null });
          return false;
        }

        const { token, refreshToken: newRefresh } = (await res.json()) as {
          token: string;
          refreshToken: string;
        };
        set({ token, refreshToken: newRefresh });
        return true;
      } catch {
        set({ token: null, refreshToken: null });
        return false;
      }
    },
  };
});
